import { Autocomplete, Box, Button, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AutocompleteIcon from "../../../assets/icons/MUI/ComponentsIcons";
import { City } from "../../../types";
import { fetchCountries } from "../../../api/network";

type Country = {
  cities: City[];
  code: string;
  id: number;
  name: string;
  nationality: string;
  shortCode: string;
  url: string;
  xCoord: number;
  yCoord: number;
};

const statusList = ["FINISHED", "SOON", "ACTIVE", "CANCELLED"];

const typeList = ["FRIENDLY", "TRAINING"];

const EventsListFilter = ({
  isLoading,
  setIsLoading,
  country,
  setCountry,
  city,
  setCity,
  status,
  setStatus,
  type,
  setType,
  clearFilters,
  hasActiveFilters,
}: {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  country: Country | null;
  setCountry: Dispatch<SetStateAction<Country | null>>;
  city: City | null;
  setCity: Dispatch<SetStateAction<City | null>>;
  status: string | null;
  setStatus: Dispatch<SetStateAction<string | null>>;
  type: string | null;
  setType: Dispatch<SetStateAction<string | null>>;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}) => {
  const { t } = useTranslation();
  const [cities, setCities] = useState<City[]>([]);

  const { data: countries } = useQuery<Country[]>({
    queryKey: ["countries"],
    queryFn: () => fetchCountries(),
    initialData: [] as Country[],
    staleTime: 0,
  });

  useEffect(() => {
    if (country) {
      const selectedCountry = countries.find((c: Country) => c.id === country.id);
      const citiesResults = selectedCountry?.cities || [];
      setCities(citiesResults);
      setCity(null);
    } else {
      setCities([]);
    }
  }, [country]);

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        whiteSpace: "nowrap",
        padding: "0 8px 8px 0",
        marginBottom: "16px",
      }}
    >
      <Box
        sx={{
          height: "40px",
          width: "fit-content",
          display: "flex",
          gap: "10px",
        }}
      >
        <Autocomplete
          options={countries || []}
          disabled={isLoading}
          value={country}
          onChange={(event, option) => {
            setCountry(option || null);
          }}
          sx={{
            width: "140px",
          }}
          popupIcon={<AutocompleteIcon />}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                style: { fontSize: "14px" },
              }}
              placeholder={t("pages.events.eventList.country")}
            />
          )}
        />
        <Autocomplete
          options={cities || []}
          disabled={isLoading}
          value={city}
          onChange={(event, option) => {
            setCity(option || null);
          }}
          sx={{
            width: "140px",
          }}
          popupIcon={<AutocompleteIcon />}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                style: { fontSize: "14px" },
              }}
              placeholder={t("pages.events.eventList.city")}
            />
          )}
        />
        <Autocomplete
          options={statusList}
          disabled={isLoading}
          value={status}
          onChange={(event, option) => {
            setStatus(option || null);
          }}
          sx={{
            width: "140px",
          }}
          popupIcon={<AutocompleteIcon />}
          getOptionLabel={(option) => t(`pages.events.eventList.${option}`)}
          isOptionEqualToValue={(option, value) => option === value}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                style: { fontSize: "14px" },
              }}
              placeholder={t("pages.events.eventList.status")}
            />
          )}
        />
        <Autocomplete
          options={typeList}
          disabled={isLoading}
          value={type}
          onChange={(event, option) => {
            setType(option || null);
          }}
          sx={{
            width: "140px",
          }}
          popupIcon={<AutocompleteIcon />}
          getOptionLabel={(option) => t(`pages.events.eventList.${option}`)}
          isOptionEqualToValue={(option, value) => option === value}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                style: { fontSize: "14px" },
              }}
              placeholder={t("pages.events.eventList.type")}
            />
          )}
        />
        {hasActiveFilters && (
          <Button disabled={isLoading} variant="text" color="primary" onClick={clearFilters}>
            {t("components.networkController.clear")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default EventsListFilter;
